import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Block as BlockIcon,
  CalendarToday as CalendarIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  ExpandMore as ExpandMoreIcon,
  Flag as FlagIcon,
  Notes as NotesIcon,
  Person as PersonIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Scale as ScaleIcon,
} from '@mui/icons-material';
import { Autocomplete, Box, Button, Chip, FormControl, IconButton, InputLabel, MenuItem, Paper, Select, TextField, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DietProgramModal from '../components/DietProgramModal';
import Loading from '../components/Loading';
import { useToast } from '../contexts/ToastContext';
import { Client, clientApi } from '../services/clientApi';
import { CreateDietProgramRequest, DailyMealPlan, DietProgram, dietProgramApi, UpdateDietProgramRequest } from '../services/dietProgramApi';
import { calculateNutritionFromFood, FoodResponse, searchFoods } from '../services/foodsApi';
import { colors } from '../theme';

const DietitianProgramDetailPage: React.FC = () => {
  const { programId } = useParams<{ programId: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [program, setProgram] = useState<DietProgram | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [localDailyMealPlans, setLocalDailyMealPlans] = useState<DailyMealPlan[]>([]);
  const [foods, setFoods] = useState<FoodResponse[]>([]);
  const [loadingFoods, setLoadingFoods] = useState(false);
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
  const mealTypes = ['Kahvaltı', 'Öğle Yemeği', 'Akşam Yemeği', 'Ara Öğün'];

  const statusChipStyle = useMemo(() => {
    const status = program?.status;
    switch (status) {
      case 'Tamamlandı':
        return { bgcolor: colors.primary.dark };
      case 'İptal Edildi':
        return { bgcolor: colors.status.error };
      case 'Ertelendi':
        return { bgcolor: colors.status.warning };
      default:
        return { bgcolor: 'rgba(255,255,255,0.25)' };
    }
  }, [program?.status]);

  useEffect(() => {
    const fetchProgramData = async () => {
      if (!programId) return;

      try {
        setLoading(true);
        const programData = await dietProgramApi.getDietProgramById(programId);
        setProgram(programData);
        setLocalDailyMealPlans(programData.dailyMealPlans || []);

        // Eğer program birden fazla client'a ait olabilirse, tüm client'ları getir
        // Şimdilik sadece program'daki clientId'yi kullanıyoruz
        if (programData.clientId) {
          try {
            const clientData = await clientApi.getClientById(
              programData.clientId
            );
            setClients([clientData]);
          } catch (err) {
            console.error('Error fetching client:', err);
          }
        }
      } catch (err) {
        console.error('Error fetching program data:', err);
        setError('Diyet programı bilgileri yüklenirken hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    fetchProgramData();
  }, [programId]);

  // Besinleri yükle
  useEffect(() => {
    const loadFoods = async () => {
      try {
        setLoadingFoods(true);
        const foodsData = await searchFoods();
        setFoods(foodsData);
      } catch (err) {
        console.error('Error loading foods:', err);
      } finally {
        setLoadingFoods(false);
      }
    };
    if (program) {
      loadFoods();
    }
  }, [program]);

  const toggleDayExpanded = (date: string) => {
    setExpandedDays((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(date)) {
        newSet.delete(date);
      } else {
        newSet.add(date);
      }
      return newSet;
    });
  };

  const addMealToDay = (date: string, mealType: string) => {
    setLocalDailyMealPlans((prev) =>
      prev.map((plan) => {
        if (plan.date === date) {
          const existingMeal = plan.meals.find((m) => m.mealType === mealType);
          if (!existingMeal) {
            return {
              ...plan,
              meals: [
                ...plan.meals,
                {
                  mealType,
                  items: [],
                  notes: '',
                },
              ],
            };
          }
        }
        return plan;
      })
    );
  };

  const addItemToMeal = async (date: string, mealType: string, food: FoodResponse, quantity: number, unit: string) => {
    const nutrition = calculateNutritionFromFood(food, quantity, unit);
    const updatedPlans = localDailyMealPlans.map((plan) => {
      if (plan.date === date) {
        return {
          ...plan,
          meals: plan.meals.map((meal) => {
            if (meal.mealType === mealType) {
              return {
                ...meal,
                items: [
                  ...meal.items,
                  {
                    foodName: food.name,
                    quantity,
                    unit,
                    calories: nutrition.calories,
                    protein: nutrition.protein,
                    carbohydrates: nutrition.carbohydrates,
                    fat: nutrition.fat,
                    notes: '',
                  },
                ],
              };
            }
            return meal;
          }),
        };
      }
      return plan;
    });
    
    setLocalDailyMealPlans(updatedPlans);
    
    // Otomatik kaydet
    if (programId) {
      try {
        const payload: UpdateDietProgramRequest = {
          dailyMealPlans: updatedPlans,
        };
        const response = await dietProgramApi.updateDietProgram(programId, payload);
        setProgram(response.dietProgram);
        setLocalDailyMealPlans(response.dietProgram.dailyMealPlans || []);
        showSuccess('Besin eklendi', 'Başarılı');
      } catch (err: any) {
        console.error('Error updating meal plans:', err);
        showError(err?.message || 'Besin eklenemedi', 'Hata');
        // Hata durumunda eski state'e geri dön
        setLocalDailyMealPlans(localDailyMealPlans);
      }
    }
  };

  const removeItemFromMeal = async (date: string, mealType: string, itemIndex: number) => {
    const updatedPlans = localDailyMealPlans.map((plan) => {
      if (plan.date === date) {
        return {
          ...plan,
          meals: plan.meals.map((meal) => {
            if (meal.mealType === mealType) {
              return {
                ...meal,
                items: meal.items.filter((_, idx) => idx !== itemIndex),
              };
            }
            return meal;
          }),
        };
      }
      return plan;
    });
    
    setLocalDailyMealPlans(updatedPlans);
    
    // Otomatik kaydet
    if (programId) {
      try {
        const payload: UpdateDietProgramRequest = {
          dailyMealPlans: updatedPlans,
        };
        const response = await dietProgramApi.updateDietProgram(programId, payload);
        setProgram(response.dietProgram);
        setLocalDailyMealPlans(response.dietProgram.dailyMealPlans || []);
        showSuccess('Besin silindi', 'Başarılı');
      } catch (err: any) {
        console.error('Error updating meal plans:', err);
        showError(err?.message || 'Besin silinemedi', 'Hata');
        // Hata durumunda eski state'e geri dön
        setLocalDailyMealPlans(localDailyMealPlans);
      }
    }
  };


  const handleRemovePdf = async () => {
    if (!programId || !program?.pdfUrl) return;
    try {
      const response = await dietProgramApi.updateDietProgram(programId, {
        pdfUrl: '',
      });
      setProgram(response.dietProgram);
      showSuccess('PDF kaldırıldı', 'Başarılı');
    } catch (err: any) {
      console.error('Error removing PDF:', err);
      showError(err?.message || 'PDF kaldırılamadı', 'Hata');
    }
  };

  const handleUpdateProgram = async (data: CreateDietProgramRequest) => {
    if (!programId) return;
    try {
      const payload: UpdateDietProgramRequest = {
        clientId: data.clientId,
        clientName: data.clientName,
        programName: data.programName,
        programType: data.programType,
        startDate: data.startDate,
        endDate: data.endDate,
        description: data.description,
        goals: data.goals,
        restrictions: data.restrictions,
        notes: data.notes,
        status: data.status,
        targetWeight: data.targetWeight,
        currentWeight: data.currentWeight,
        pdfUrl: data.pdfUrl,
        dailyMealPlans: data.dailyMealPlans,
      };

      const response = await dietProgramApi.updateDietProgram(programId, payload);
      setProgram(response.dietProgram);

      // Client bilgisini güncelle
      if (payload.clientId && payload.clientId !== program?.clientId) {
        try {
          const updatedClient = await clientApi.getClientById(payload.clientId);
          setClients([updatedClient]);
        } catch (err) {
          console.error('Error fetching updated client:', err);
        }
      }

      showSuccess('Diyet programı güncellendi', 'Güncelleme Başarılı');
      setIsEditModalOpen(false);
    } catch (err: any) {
      console.error('Error updating program:', err);
      const errorMessage = err?.message || 'Diyet programı güncellenemedi';
      showError(errorMessage, 'Hata');
      throw err;
    }
  };

  if (loading) {
    return (
      <Loading
        size="large"
        variant="wave"
        mode="inline"
        message="Diyet programı detayları yükleniyor..."
      />
    );
  }

  if (error || !program) {
    return (
      <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="error" gutterBottom>
          {error || 'Diyet programı bulunamadı'}
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dietitian/programs')}
          sx={{
            mt: 2,
            bgcolor: colors.primary.main,
            '&:hover': { bgcolor: colors.hover.primary },
          }}
        >
          Diyet Programları Listesine Dön
        </Button>
      </Paper>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100vw',
        overflowX: 'auto',
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 1.5,
          borderRadius: 1.5,
          background: 'linear-gradient(135deg, #43A047 0%, #2E7D32 100%)',
          color: 'white',
          boxShadow: '0 4px 12px rgba(46, 125, 50, 0.25)',
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={0.5}
          gap={0.75}
        >
          <Box display="flex" alignItems="center" gap={0.75}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/dietitian/programs')}
              sx={{
                color: 'white',
                textTransform: 'none',
                px: 0.5,
                minHeight: 32,
                minWidth: 36,
                borderRadius: 1.25,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.12)',
                },
              }}
            ></Button>
            <Box
              display="flex"
              alignItems="center"
              gap={0.5}
              sx={{ minWidth: 140 }}
            >
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ color: 'white', lineHeight: 1.2 }}
              >
                {program.programName}
              </Typography>
            </Box>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ minWidth: 110, justifyContent: 'flex-end' }}
          >
            {program.status && (
              <Chip
                label={program.status}
                size="small"
                sx={{
                  ...statusChipStyle,
                  color: 'white',
                  fontWeight: 700,
                  letterSpacing: 0.2,
                  height: 26,
                  borderRadius: 999,
                  px: 2,
                  mr: 1,
                }}
              />
            )}
            {program.programType && (
              <Chip
                label={program.programType}
                size="small"
                sx={{
                  height: 22,
                  borderRadius: 999,
                  bgcolor: 'rgba(255,255,255,0.18)',
                  color: 'white',
                  fontWeight: 600,
                  px: 1,
                }}
              />
            )}
          </Box>
        </Box>
      </Paper>

      {/* Diyet Programı Detayları */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 2.5,
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          sx={{ mb: 2 }}
        >
          <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 0 }}>
            Detaylar
          </Typography>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => setIsEditModalOpen(true)}
            sx={{
              bgcolor: colors.primary.main,
              color: 'white',
              textTransform: 'none',
              boxShadow: '0 2px 6px rgba(67,160,71,0.2)',
              minHeight: 34,
              width: 'fit-content',
              px: 1.5,
              '&:hover': { bgcolor: colors.hover.primary },
            }}
          >
            Düzenle
          </Button>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)',
            },
            gap: 1.5,
            mb: 2,
          }}
        >
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1.5,
              bgcolor: colors.background.light,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <CalendarIcon sx={{ fontSize: 18, color: colors.primary.main }} />
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={500}
              >
                Başlangıç
              </Typography>
            </Box>
            <Typography variant="body1" fontWeight={600}>
              {new Date(program.startDate).toLocaleDateString('tr-TR')}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 1.5,
              borderRadius: 1.5,
              bgcolor: colors.background.light,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <CalendarIcon sx={{ fontSize: 18, color: colors.primary.main }} />
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={500}
              >
                Bitiş
              </Typography>
            </Box>
            <Typography variant="body1" fontWeight={600}>
              {new Date(program.endDate).toLocaleDateString('tr-TR')}
            </Typography>
          </Box>

          {program.targetWeight && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: colors.background.light,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <ScaleIcon sx={{ fontSize: 18, color: colors.primary.main }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={500}
                >
                  Hedef
                </Typography>
              </Box>
              <Typography variant="body1" fontWeight={600}>
                {program.targetWeight} kg
              </Typography>
            </Box>
          )}

          {program.currentWeight && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: colors.background.light,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <ScaleIcon sx={{ fontSize: 18, color: colors.primary.main }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={500}
                >
                  Mevcut
                </Typography>
              </Box>
              <Typography variant="body1" fontWeight={600}>
                {program.currentWeight} kg
              </Typography>
            </Box>
          )}
        </Box>

        {program.description && (
          <Box
            sx={{
              p: 2,
              mb: 1.5,
              borderRadius: 1.5,
              bgcolor: colors.background.light,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <DescriptionIcon
                sx={{ fontSize: 18, color: colors.primary.main }}
              />
              <Typography variant="subtitle2" fontWeight={600}>
                Açıklama
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {program.description}
            </Typography>
          </Box>
        )}

        {program.goals && (
          <Box
            sx={{
              p: 2,
              mb: 1.5,
              borderRadius: 1.5,
              bgcolor: colors.background.light,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <FlagIcon sx={{ fontSize: 18, color: colors.primary.main }} />
              <Typography variant="subtitle2" fontWeight={600}>
                Hedefler
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {program.goals}
            </Typography>
          </Box>
        )}

        {program.restrictions && (
          <Box
            sx={{
              p: 2,
              mb: 1.5,
              borderRadius: 1.5,
              bgcolor: colors.background.light,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <BlockIcon sx={{ fontSize: 18, color: colors.primary.main }} />
              <Typography variant="subtitle2" fontWeight={600}>
                Kısıtlamalar
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {program.restrictions}
            </Typography>
          </Box>
        )}

        {program.notes && (
          <Box
            sx={{
              p: 2,
              mb: 1.5,
              borderRadius: 1.5,
              bgcolor: colors.background.light,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <NotesIcon sx={{ fontSize: 18, color: colors.primary.main }} />
              <Typography variant="subtitle2" fontWeight={600}>
                Notlar
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {program.notes}
            </Typography>
          </Box>
        )}

        {/* PDF Bölümü */}
        <Box
          sx={{
            p: 2,
            borderRadius: 1.5,
            bgcolor: colors.background.light,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <PictureAsPdfIcon sx={{ fontSize: 18, color: colors.primary.main }} />
            <Typography variant="subtitle2" fontWeight={600}>
              Ek Dosya
            </Typography>
          </Box>
          {program.pdfUrl ? (
            <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
              <Button
                size="small"
                variant="outlined"
                onClick={() => window.open(program.pdfUrl!, '_blank')}
                sx={{
                  textTransform: 'none',
                  borderColor: colors.primary.main,
                  color: colors.primary.main,
                  '&:hover': {
                    borderColor: colors.primary.dark,
                    bgcolor: colors.alpha.primary10,
                  },
                }}
              >
                Görüntüle
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = program.pdfUrl!;
                  const pathPart = program.pdfUrl!.split('?')[0];
                  const ext = pathPart.match(/\.([a-zA-Z0-9]+)$/)?.[1] || 'pdf';
                  link.download = `${program.programName}.${ext}`;
                  link.click();
                }}
                sx={{
                  textTransform: 'none',
                  borderColor: colors.primary.main,
                  color: colors.primary.main,
                  '&:hover': {
                    borderColor: colors.primary.dark,
                    bgcolor: colors.alpha.primary10,
                  },
                }}
              >
                İndir
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon fontSize="small" />}
                onClick={handleRemovePdf}
                sx={{ textTransform: 'none' }}
              >
                Dosyayı Sil
              </Button>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Dosya yüklemek için Düzenle butonuna tıklayın.
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Günlük Yemek Planları */}
      {localDailyMealPlans && localDailyMealPlans.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 2.5,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          <Typography variant="h6" fontWeight={700} mb={2}>
            Günlük Yemek Planları
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {localDailyMealPlans.map((dailyPlan) => {
              const date = new Date(dailyPlan.date);
              const dateStr = date.toLocaleDateString('tr-TR', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              });
              const isExpanded = expandedDays.has(dailyPlan.date);
              const units = ['gram', 'porsiyon', 'adet', 'ml', 'su bardağı', 'dilim', 'kase', 'fincan', 'yemek kaşığı', 'salkım'];

              const AddFoodToMeal: React.FC<{
                date: string;
                mealType: string;
              }> = ({ date, mealType }) => {
                const [selectedFood, setSelectedFood] = useState<FoodResponse | null>(null);
                const [quantity, setQuantity] = useState<number>(100);
                const [unit, setUnit] = useState<string>('gram');

                // Besin seçildiğinde standart birimini otomatik seç
                const handleFoodSelect = (food: FoodResponse | null) => {
                  setSelectedFood(food);
                  if (food?.standardUnit && food?.standardUnitWeight) {
                    setUnit(food.standardUnit);
                    setQuantity(1); // Standart birim için varsayılan: 1
                  } else {
                    setUnit('gram');
                    setQuantity(100);
                  }
                };

                const handleAdd = () => {
                  if (selectedFood) {
                    addItemToMeal(date, mealType, selectedFood, quantity, unit);
                    setSelectedFood(null);
                    setQuantity(100);
                    setUnit('gram');
                  }
                };

                return (
                  <Box 
                    sx={{ 
                      mt: 1.5,
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Autocomplete
                        options={foods}
                        getOptionLabel={(option) => option.name}
                        loading={loadingFoods}
                        value={selectedFood}
                        onChange={(_, newValue) => handleFoodSelect(newValue)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Besin Seç"
                            size="small"
                            placeholder="Besin ara..."
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 1.5,
                                bgcolor: colors.background.light,
                                '&:hover': {
                                  bgcolor: 'white',
                                },
                                '&.Mui-focused': {
                                  bgcolor: 'white',
                                },
                              },
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>{option.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {option.calories} kcal/100g • P: {option.protein}g • K: {option.carbohydrates}g • Y: {option.fat}g
                                {option.standardUnit && option.standardUnitWeight && (
                                  <> • ⭐ {option.standardUnit} = {option.standardUnitWeight}g</>
                                )}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      />
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          gap: 1,
                        }}
                      >
                        <TextField
                          label="Miktar"
                          type="number"
                          size="small"
                          value={quantity}
                          onChange={(e) => setQuantity(Number(e.target.value))}
                          sx={{ 
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1.5,
                              bgcolor: colors.background.light,
                              '&:hover': {
                                bgcolor: 'white',
                              },
                              '&.Mui-focused': {
                                bgcolor: 'white',
                              },
                            },
                          }}
                          InputProps={{
                            inputProps: { 
                              min: 0,
                              step: 1,
                            }
                          }}
                        />
                        <FormControl 
                          size="small" 
                          sx={{ 
                            minWidth: 140,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1.5,
                              bgcolor: colors.background.light,
                              '&:hover': {
                                bgcolor: 'white',
                              },
                              '&.Mui-focused': {
                                bgcolor: 'white',
                              },
                            },
                          }}
                        >
                          <InputLabel>Birim</InputLabel>
                          <Select 
                            value={unit} 
                            onChange={(e) => setUnit(e.target.value)} 
                            label="Birim"
                          >
                            {/* Standart birim varsa önce onu göster */}
                            {selectedFood?.standardUnit && selectedFood?.standardUnitWeight && (
                              <MenuItem value={selectedFood.standardUnit}>
                                {selectedFood.standardUnit} ({selectedFood.standardUnitWeight}g) ⭐
                              </MenuItem>
                            )}
                            {units.map((u) => (
                              <MenuItem key={u} value={u}>
                                {u}
                              </MenuItem>
                            ))}
                          </Select>
                          {selectedFood?.standardUnit && selectedFood?.standardUnitWeight && (
                            <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary', fontSize: '0.7rem', display: 'block' }}>
                              💡 Önerilen: {selectedFood.standardUnit} = {selectedFood.standardUnitWeight}g
                            </Typography>
                          )}
                        </FormControl>
                      </Box>
                      {selectedFood && quantity > 0 && (
                        <Box 
                          sx={{ 
                            p: 1, 
                            bgcolor: colors.alpha.primary10,
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            Tahmini: <strong>{calculateNutritionFromFood(selectedFood, quantity, unit).calories} kcal</strong>
                            {' • '}
                            P: <strong>{calculateNutritionFromFood(selectedFood, quantity, unit).protein.toFixed(1)}g</strong>
                            {' • '}
                            K: <strong>{calculateNutritionFromFood(selectedFood, quantity, unit).carbohydrates.toFixed(1)}g</strong>
                            {' • '}
                            Y: <strong>{calculateNutritionFromFood(selectedFood, quantity, unit).fat.toFixed(1)}g</strong>
                          </Typography>
                        </Box>
                      )}
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleAdd}
                        disabled={!selectedFood || quantity <= 0}
                        fullWidth
                        sx={{
                          textTransform: 'none',
                          bgcolor: colors.primary.main,
                          color: 'white',
                          borderRadius: 1.5,
                          py: 1,
                          fontWeight: 600,
                          boxShadow: `0 2px 8px ${colors.alpha.primary30}`,
                          '&:hover': { 
                            bgcolor: colors.primary.dark,
                            boxShadow: `0 4px 12px ${colors.alpha.primary30}`,
                          },
                          '&:disabled': {
                            bgcolor: colors.border.light,
                            color: colors.text.secondary,
                            boxShadow: 'none',
                          },
                        }}
                      >
                        Besin Ekle
                      </Button>
                    </Box>
                  </Box>
                );
              };

              return (
                <Box
                  key={dailyPlan.date}
                  sx={{
                    border: `1px solid ${colors.border.light}`,
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    onClick={() => toggleDayExpanded(dailyPlan.date)}
                    sx={{
                      p: 1.5,
                      bgcolor: colors.background.light,
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      '&:hover': {
                        bgcolor: colors.alpha.primary10,
                      },
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={600}>
                      {dateStr}
                    </Typography>
                    <ExpandMoreIcon
                      sx={{
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                        color: colors.text.secondary,
                      }}
                    />
                  </Box>
                  {isExpanded && (
                    <Box sx={{ p: 2 }}>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, 1fr)',
                            md: 'repeat(3, 1fr)',
                            lg: 'repeat(4, 1fr)',
                          },
                          gap: 2,
                        }}
                      >
                        {mealTypes.map((mealType) => {
                          const meal = dailyPlan.meals.find((m) => m.mealType === mealType);
                          const mealCalories = meal?.items.reduce((sum, item) => sum + item.calories, 0) || 0;
                          const mealProtein = meal?.items.reduce((sum, item) => sum + item.protein, 0) || 0;
                          const mealCarbs = meal?.items.reduce((sum, item) => sum + item.carbohydrates, 0) || 0;
                          const mealFat = meal?.items.reduce((sum, item) => sum + item.fat, 0) || 0;

                          return (
                            <Paper
                              key={mealType}
                              elevation={0}
                              sx={{
                                p: 1.5,
                                border: `1px solid ${colors.border.light}`,
                                borderRadius: 1.5,
                                bgcolor: 'white',
                                display: 'flex',
                                flexDirection: 'column',
                                minHeight: 200,
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  mb: 1.5,
                                  pb: 1,
                                  borderBottom: `2px solid ${colors.primary.light}`,
                                }}
                              >
                                <Typography variant="subtitle2" fontWeight={700} sx={{ color: colors.primary.main }}>
                                  {mealType}
                                </Typography>
                                {mealCalories > 0 && (
                                  <Chip
                                    label={`${mealCalories} kcal`}
                                    size="small"
                                    sx={{
                                      bgcolor: colors.primary.light,
                                      color: colors.primary.main,
                                      fontWeight: 600,
                                    }}
                                  />
                                )}
                              </Box>
                              {meal && meal.items.length > 0 ? (
                                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1 }}>
                                  {meal.items.map((item, itemIndex) => (
                                    <Box
                                      key={itemIndex}
                                      sx={{
                                        p: 1,
                                        bgcolor: colors.background.light,
                                        borderRadius: 1,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                      }}
                                    >
                                      <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.875rem' }}>
                                          {item.foodName}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block', mt: 0.25, fontSize: '0.7rem' }}
                                        >
                                          {item.quantity} {item.unit} • {item.calories} kcal
                                        </Typography>
                                      </Box>
                                      <IconButton
                                        size="small"
                                        onClick={() => removeItemFromMeal(dailyPlan.date, mealType, itemIndex)}
                                        sx={{ color: colors.status.error, ml: 0.5, p: 0.5, width: 'fit-content' }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  ))}
                                  <Box
                                    sx={{
                                      mt: 'auto',
                                      pt: 1,
                                      borderTop: `1px solid ${colors.border.light}`,
                                      display: 'flex',
                                      gap: 0.5,
                                      flexWrap: 'wrap',
                                    }}
                                  >
                                    <Chip
                                      label={`P: ${mealProtein.toFixed(1)}g`}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.7rem',
                                        bgcolor: colors.alpha.primary10,
                                      }}
                                    />
                                    <Chip
                                      label={`K: ${mealCarbs.toFixed(1)}g`}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.7rem',
                                        bgcolor: colors.alpha.primary10,
                                      }}
                                    />
                                    <Chip
                                      label={`Y: ${mealFat.toFixed(1)}g`}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.7rem',
                                        bgcolor: colors.alpha.primary10,
                                      }}
                                    />
                                  </Box>
                                </Box>
                              ) : (
                                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    Henüz besin eklenmemiş
                                  </Typography>
                                </Box>
                              )}
                              {meal ? (
                                <AddFoodToMeal date={dailyPlan.date} mealType={mealType} />
                              ) : (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<AddIcon />}
                                  onClick={() => addMealToDay(dailyPlan.date, mealType)}
                                  fullWidth
                                  sx={{
                                    textTransform: 'none',
                                    borderColor: colors.primary.main,
                                    color: colors.primary.main,
                                    mt: 'auto',
                                    '&:hover': {
                                      borderColor: colors.primary.dark,
                                      bgcolor: colors.alpha.primary10,
                                    },
                                  }}
                                >
                                  Öğün Ekle
                                </Button>
                              )}
                            </Paper>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}

      {/* İlgili Danışanlar */}
      {clients.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
            Danışan
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            {clients.map((client) => (
              <Box
                key={client.id}
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  bgcolor: colors.background.light,
                  border: '1px solid',
                  borderColor: 'divider',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: colors.hover.background,
                    borderColor: colors.primary.main,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  },
                }}
                onClick={() => navigate(`/dietitian/clients/${client.id}`)}
              >
                <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: colors.primary.main,
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                    }}
                  >
                    <PersonIcon />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {client.firstName} {client.lastName}
                    </Typography>
                  </Box>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <EmailIcon
                    sx={{ fontSize: 16, color: colors.text.secondary }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {client.email}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* Düzenleme Modal */}
      <DietProgramModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleUpdateProgram}
        clients={clients.map(client => ({
          id: client.id,
          firstName: client.firstName,
          lastName: client.lastName,
          phone: client.phone || '',
          email: client.email,
          age: client.age || 0,
          gender: client.gender || '',
          goal: client.goal || '',
          notes: client.notes || '',
          createdAt: client.createdAt ? new Date(client.createdAt) : new Date(),
        }))}
        editingProgram={program}
      />
    </Box>
  );
};

export default DietitianProgramDetailPage;
